/**
 * Canvas-based Mapbox marker images, shared map helper. Mirrors the asset map's
 * marker builders but exposes a small, reusable surface. The tracker map uses a
 * trimmed set of statuses (ok / stale / warning) instead of alarm severities.
 */

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

type RGB = [number, number, number]

function hexToRgb(hex: string): RGB {
  let h = hex.replace('#', '').trim()
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const num = parseInt(h, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

const lerpColor = (from: RGB, to: RGB, t: number) =>
  `rgb(${Math.round(lerp(from[0], to[0], t))}, ${Math.round(lerp(from[1], to[1], t))}, ${Math.round(lerp(from[2], to[2], t))})`

interface CanvasMarker {
  width: number
  height: number
  data: Uint8Array
  context?: CanvasRenderingContext2D
  onAdd(): void
  render(): boolean
}

const addLabelBackground = (map: mapboxgl.Map, id: string, fill: string, stroke: string, strokeWidth = 1) => {
  if (map.hasImage(id)) return
  const size = 32
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.beginPath()
  ctx.roundRect(3, 3, size - 6, size - 6, 10)
  ctx.shadowColor = 'rgba(15, 23, 42, 0.25)'
  ctx.shadowBlur = 4
  ctx.shadowOffsetY = 1
  ctx.fillStyle = fill
  ctx.fill()
  ctx.shadowColor = 'transparent'
  ctx.strokeStyle = stroke
  ctx.lineWidth = strokeWidth
  ctx.stroke()
  map.addImage(id, ctx.getImageData(0, 0, size, size), {
    pixelRatio: 2,
    stretchX: [[13, 19]],
    stretchY: [[13, 19]],
    content: [13, 13, 19, 19],
  })
}

export const addLabelBackgrounds = (map: mapboxgl.Map) => {
  addLabelBackground(map, 'label-bg-light', 'rgba(255, 255, 255, 0.96)', 'rgba(15, 23, 42, 0.12)')
  addLabelBackground(map, 'label-bg-dark', 'rgba(15, 23, 42, 0.88)', 'rgba(255, 255, 255, 0.18)')
  addLabelBackground(map, 'label-bg-light-selected', 'rgba(255, 255, 255, 0.96)', '#083c8a', 2)
  addLabelBackground(map, 'label-bg-dark-selected', 'rgba(15, 23, 42, 0.88)', '#60a5fa', 2)
}

const createAnimatedMarker = (map: mapboxgl.Map, color = '#3b82f6'): CanvasMarker => {
  const size = 96
  return {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),
    onAdd() {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      this.context = canvas.getContext('2d', { willReadFrequently: true })!
    },
    render() {
      const ctx = this.context!
      const t = (performance.now() % 2400) / 2400
      const pulse = t < 0.5 ? t * 2 : 2 - t * 2
      const innerColor = lerpColor([255, 255, 255], hexToRgb(color), pulse)

      ctx.clearRect(0, 0, size, size)
      const cx = size / 2
      const cy = size / 2 + 6

      ctx.beginPath()
      ctx.moveTo(cx, cy - 28)
      ctx.bezierCurveTo(cx - 20, cy - 28, cx - 32, cy - 8, cx, cy + 30)
      ctx.bezierCurveTo(cx + 32, cy - 8, cx + 20, cy - 28, cx, cy - 28)
      ctx.closePath()
      ctx.fillStyle = color
      ctx.strokeStyle = innerColor
      ctx.lineWidth = 4
      ctx.fill()
      ctx.stroke()

      ctx.beginPath()
      ctx.arc(cx, cy - 6, 7, 0, Math.PI * 2)
      ctx.fillStyle = 'white'
      ctx.fill()

      this.data = new Uint8Array(ctx.getImageData(0, 0, size, size).data)
      map.triggerRepaint()
      return true
    },
  }
}

export const startRepaintLoop = (map: mapboxgl.Map) => {
  const repaint = () => {
    map.triggerRepaint()
    requestAnimationFrame(repaint)
  }
  repaint()
}

// Static marker webp + matching animated (selected) variant per tracker status.
const markerImages = ['default-marker', 'offline-marker', 'warning-marker']
const markerColors = ['#3b82f6', '#999999', '#eab308']

const MARKER_CSS_SIZE = 38
const MARKER_PIXEL_RATIO = 3

const loadMarkerImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Bad image: ${url}`))
    image.src = url
  })

const rasterizeMarker = (image: HTMLImageElement): ImageData => {
  const size = MARKER_CSS_SIZE * MARKER_PIXEL_RATIO
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas context not available')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(image, 0, 0, size, size)
  return ctx.getImageData(0, 0, size, size)
}

export const addStaticMarkerImage = async (map: mapboxgl.Map, imageName: string) => {
  const image = await loadMarkerImage(`/images/${imageName}.webp`)
  if (!map.hasImage(imageName)) {
    map.addImage(imageName, rasterizeMarker(image), { pixelRatio: MARKER_PIXEL_RATIO })
  }
}

export const addTrackerMarkerImages = (map: mapboxgl.Map) => {
  markerImages.forEach((imageName, idx) => {
    if (!map.hasImage(`animated-${imageName}`)) {
      map.addImage(`animated-${imageName}`, createAnimatedMarker(map, markerColors[idx]), { pixelRatio: 2 })
    }
    if (map.hasImage(imageName)) return
    void addStaticMarkerImage(map, imageName)
  })
}
